import { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import VehicleCard from "@/components/VehicleCard";
import { Loader2, Search, ArrowUpDown, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FILTERS = [
  { key: "all", label: "Tutti" },
  { key: "scooter", label: "Scooter" },
  { key: "moto", label: "Moto" },
  { key: "auto_usata", label: "Auto Usate" },
  { key: "auto_nuova", label: "Auto Nuove" },
];

/**
 * Match a vehicle against a filter key.
 * Supports both new schema (type=auto/scooter/moto + condition=Nuovo/Usato)
 * AND legacy schema (type=scooter_cabinato/auto_usata/auto_nuova) for
 * backward compatibility with already-published listings.
 */
function matchFilter(v, key) {
  if (key === "all") return true;
  const t = (v.type || "").toLowerCase();
  const c = (v.condition || "").toLowerCase();
  if (key === "scooter") return t === "scooter" || t === "scooter_cabinato";
  if (key === "moto") return t === "moto";
  if (key === "auto_usata") return c === "usato" || t === "auto_usata";
  if (key === "auto_nuova") return c === "nuovo" || t === "auto_nuova";
  return false;
}

const SORTS = [
  { key: "newest", label: "Più recenti" },
  { key: "price_asc", label: "Prezzo crescente" },
  { key: "price_desc", label: "Prezzo decrescente" },
  { key: "year_desc", label: "Anno (nuovi)" },
  { key: "km_asc", label: "Km crescenti" },
];

export default function VehicleGrid({
  id = "vetrina",
  limit = null,
  showControls = true,
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(searchParams.get("type") || "all");
  const [sort, setSort] = useState("newest");
  const [search, setSearch] = useState(searchParams.get("brand") || "");
  const [fuel, setFuel] = useState(searchParams.get("fuel") || "");

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    axios
      .get(`${API}/vehicles`)
      .then((res) => mounted && setVehicles(res.data || []))
      .catch((e) => console.error("vehicles load error", e))
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  // Sync URL search params back into UI when changed externally
  useEffect(() => {
    const t = searchParams.get("type");
    if (t && t !== filter) setFilter(t);
    const b = searchParams.get("brand");
    if (b && b !== search) setSearch(b);
    const f = searchParams.get("fuel");
    if (f && f !== fuel) setFuel(f);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const updateFilter = (key) => {
    setFilter(key);
    const sp = new URLSearchParams(searchParams);
    if (key === "all") sp.delete("type");
    else sp.set("type", key);
    setSearchParams(sp, { replace: true });
  };

  const filtered = useMemo(() => {
    let list = vehicles.filter((v) => matchFilter(v, filter));
    if (search) {
      const s = search.toLowerCase().trim();
      list = list.filter(
        (v) =>
          v.brand.toLowerCase().includes(s) ||
          v.model.toLowerCase().includes(s) ||
          (v.color || "").toLowerCase().includes(s)
      );
    }
    if (fuel) {
      list = list.filter((v) => v.fuel === fuel);
    }
    const sorted = [...list];
    switch (sort) {
      case "price_asc":
        sorted.sort((a, b) => a.price - b.price);
        break;
      case "price_desc":
        sorted.sort((a, b) => b.price - a.price);
        break;
      case "year_desc":
        sorted.sort((a, b) => b.year - a.year);
        break;
      case "km_asc":
        sorted.sort((a, b) => a.km - b.km);
        break;
      default:
        break;
    }
    return limit ? sorted.slice(0, limit) : sorted;
  }, [vehicles, filter, sort, search, fuel, limit]);

  const hasUrlFilters = Boolean(
    searchParams.get("brand") ||
      searchParams.get("fuel") ||
      searchParams.get("type")
  );

  const clearAll = () => {
    setFilter("all");
    setSearch("");
    setFuel("");
    setSort("newest");
    setSearchParams({}, { replace: true });
  };

  return (
    <section
      id={id}
      data-testid="vetrina-section"
      className="relative py-20 md:py-28 bg-[#f5f6f4]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#5ab500] font-bold mb-3">
              — La Vetrina
            </div>
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#1c1f1a]">
              I nostri <span className="italic text-[#5ab500]">veicoli</span>
            </h2>
            <p className="mt-3 text-[#7a7f77] max-w-xl">
              {filtered.length} risultat{filtered.length === 1 ? "o" : "i"}
              {hasUrlFilters && (
                <button
                  onClick={clearAll}
                  data-testid="vetrina-clear-filters"
                  className="ml-3 inline-flex items-center gap-1 text-[#5ab500] hover:text-[#4a9a00] underline-offset-2 hover:underline"
                >
                  <X size={12} /> Rimuovi filtri
                </button>
              )}
            </p>
          </div>
        </div>

        {showControls && (
          <div className="mb-10 flex flex-col lg:flex-row gap-4 lg:items-center lg:justify-between">
            <div
              data-testid="filter-pills"
              className="flex flex-wrap gap-2 p-1.5 rounded-full bg-white border border-black/[0.06] shadow-sm w-fit"
            >
              {FILTERS.map((f) => {
                const active = filter === f.key;
                return (
                  <button
                    key={f.key}
                    data-testid={`filter-pill-${f.key}`}
                    onClick={() => updateFilter(f.key)}
                    className={`px-5 py-2 rounded-full text-xs font-bold uppercase tracking-wider transition-all duration-300 ${
                      active
                        ? "bg-[#5ab500] text-white shadow-[0_4px_14px_rgba(90,181,0,0.4)]"
                        : "text-[#4a4f47] hover:bg-[#f5f6f4]"
                    }`}
                  >
                    {f.label}
                  </button>
                );
              })}
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative">
                <Search
                  size={14}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7a7f77]"
                />
                <input
                  data-testid="vetrina-search"
                  placeholder="Cerca marca, modello…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9 pr-4 py-2.5 rounded-full bg-white border border-black/[0.08] text-sm placeholder-[#7a7f77] focus:border-[#5ab500] outline-none w-full sm:w-64 shadow-sm"
                />
              </div>
              <div className="relative">
                <ArrowUpDown
                  size={14}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7a7f77] pointer-events-none"
                />
                <select
                  data-testid="vetrina-sort"
                  value={sort}
                  onChange={(e) => setSort(e.target.value)}
                  className="pl-9 pr-9 py-2.5 rounded-full bg-white border border-black/[0.08] text-sm text-[#1c1f1a] focus:border-[#5ab500] outline-none appearance-none cursor-pointer shadow-sm"
                >
                  {SORTS.map((s) => (
                    <option key={s.key} value={s.key}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-24 text-[#7a7f77]">
            <Loader2 className="animate-spin" />
            <span className="ml-3">Caricamento veicoli…</span>
          </div>
        ) : filtered.length === 0 ? (
          <div
            data-testid="empty-vetrina"
            className="text-center py-24 text-[#7a7f77] bg-white rounded-2xl border border-black/[0.05]"
          >
            Nessun veicolo trovato per i filtri selezionati.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {filtered.map((v) => (
              <VehicleCard key={v.id} vehicle={v} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
